import styled from 'styled-components';
import {
  Order,
  RfqOrder,
} from '@paradigm/features/src/unified-rfqs/entities/drfq/orders';
import { Side } from '@paradigm/features/src/unified-rfqs/entities/shared';
import { useQuoteBuilderModalMethods } from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/modal-context';
import { useDrfqRfq } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';

import { isMarkOrBetter } from '@paradigm/features/src/unified-rfqs/ui/rfq-websocket/mark-price-context';
import {
  lima,
  paradigmLightBlue,
  razzmatazz,
} from '@paradigm/design-system/src/colors';
import { useFormattedDrfqMakerPrice } from '@paradigm/features/src/unified-rfqs/ui/hooks/useFormattedPrice';
import { useUMPlatformCreationDisabled } from '@paradigm/features/src/platform-state/contexts/providers';
import { EmptyPrice, PriceButton } from '../../rfq-common/ActionButtons';
import { stopPropagation } from './common';

interface Props {
  readonly order?: Order | RfqOrder | null | undefined;
  readonly onClick?: () => void;
  readonly side: Side;
  readonly mark?: string | null;
  readonly hidePlaceholder?: boolean;
  readonly disabled: boolean;
}

export default function OrderPriceButton({
  order,
  side,
  onClick,
  mark,
  hidePlaceholder = false,
}: Props) {
  const isPlatformCreationDisabled = useUMPlatformCreationDisabled();

  const drfq = useDrfqRfq();
  const handleClick = stopPropagation(onClick);
  const isBuy = side === 'BUY';
  const orderRole =
    /**
     * Check if current order is from TAKER
     * - We display Taker prices in Trade Recap modal
     * */
    isRfqOrder(order) && order.desk === drfq.taker_desk_name
      ? 'TAKER'
      : 'MAKER';
  const formattedPrice = useFormattedDrfqMakerPrice({
    price: order?.price,
    side,
    drfqInfo: {
      ...drfq,
      role: orderRole,
    },
  });

  if (order == null)
    return (
      <EmptyPrice
        disabled={isPlatformCreationDisabled}
        onClick={handleClick}
        hidePlaceholder={hidePlaceholder}
      />
    );

  const buttonProps = {
    isBetterOrEqual: isMarkOrBetter(mark ?? null, order.price, side),
    children: formattedPrice,
    onClick: handleClick,
    'aria-label': isBuy ? 'bid' : 'ask',
  };

  if (isBuy) {
    return (
      <StyledBidButton disabled={isPlatformCreationDisabled} {...buttonProps} />
    );
  }

  return (
    <StyledAskButton disabled={isPlatformCreationDisabled} {...buttonProps} />
  );
}

const isRfqOrder = (
  order: Order | RfqOrder | null | undefined,
): order is RfqOrder => order != null && 'desk' in order;

export function MakerPriceButton(props: Props) {
  const drfq = useDrfqRfq();
  const { open } = useQuoteBuilderModalMethods();
  const handleClick = () => open({ rfqId: drfq.id, side: props.side });

  return <OrderPriceButton {...props} onClick={handleClick} />;
}

export function TakerPriceButton(props: Props) {
  return <OrderPriceButton {...props} />;
}

const StyledBidButton = styled(PriceButton)<{ isBetterOrEqual: boolean }>`
  color: ${lima};
  border: 1px solid
    ${(props) => (props.isBetterOrEqual ? paradigmLightBlue : 'transparent')};
`;

const StyledAskButton = styled(PriceButton)<{ isBetterOrEqual: boolean }>`
  color: ${razzmatazz};
  border: 1px solid
    ${(props) => (props.isBetterOrEqual ? paradigmLightBlue : 'transparent')};
`;
